import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import inventoryService, { ProductionAssignment } from '../../services/inventoryService';
import { wsService } from '../../services/websocket';
import { formatDateTime } from '../../utils/dateUtils';
import { getStatusBadgeClass } from '../../utils/statusColors';
import { useLanguage } from '../../i18n/LanguageContext';

const ProductionAssignments: React.FC = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [locationFilter, setLocationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'completed' | ''>('active');

  // Fetch production assignments
  const { data: assignments, isLoading, error } = useQuery(
    ['production-assignments', locationFilter, statusFilter],
    () => inventoryService.getProductionAssignments({
      location: locationFilter || undefined,
      status: statusFilter || undefined,
    })
  );

  // WebSocket for real-time updates
  useEffect(() => {
    wsService.connect();

    const handleSessionUpdated = () => {
      queryClient.invalidateQueries(['production-assignments']);
    };

    wsService.on('production-session-updated', handleSessionUpdated);
    wsService.on('production-session-created', handleSessionUpdated);
    wsService.on('station-updated', handleSessionUpdated);

    return () => {
      wsService.off('production-session-updated', handleSessionUpdated);
      wsService.off('production-session-created', handleSessionUpdated);
      wsService.off('station-updated', handleSessionUpdated);
    };
  }, [queryClient]);

  // Get unique locations
  const locations = React.useMemo(() => {
    if (!assignments) return [];
    const uniqueLocations = new Set(assignments.map((a: ProductionAssignment) => a.location_code).filter(Boolean));
    return Array.from(uniqueLocations).sort();
  }, [assignments]);

  // Group assignments by station
  const groupedAssignments = React.useMemo(() => {
    if (!assignments) return {};
    return assignments.reduce((acc: Record<string, ProductionAssignment[]>, assignment: ProductionAssignment) => {
      const key = assignment.station_name || 'Unassigned';
      if (!acc[key]) acc[key] = [];
      acc[key].push(assignment);
      return acc;
    }, {});
  }, [assignments]);


  const getProgressPercentage = (actual: number, target: number) => {
    if (!target) return '0.00';
    return Math.min(100, (actual / target) * 100).toFixed(2);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Production Assignments</h1>
        <p className="mt-1 text-sm text-gray-500">
          Monitor production jobs and worker assignments across all stations
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <select
              id="location"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Locations</option>
              {locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'active' | 'completed' | '')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div className="flex items-end">
            <div className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-3">
              <div className="text-xs text-gray-500">Total Assignments</div>
              <div className="text-2xl font-bold text-gray-900">{assignments?.length || 0}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Production Assignments by Station */}
      {isLoading ? (
        <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
          Loading production assignments...
        </div>
      ) : error ? (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center text-red-500 dark:text-red-400">
          Failed to load production assignments
        </div>
      ) : Object.keys(groupedAssignments).length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
          No production assignments found
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedAssignments).map(([stationName, stationAssignments]) => (
            <div key={stationName} className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
              {/* Station Header */}
              <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{stationName}</h2>
                    <p className="text-sm text-gray-500">
                      {stationAssignments.length} active {stationAssignments.length === 1 ? 'job' : 'jobs'}
                    </p>
                  </div>
                  <div className="text-sm text-gray-500">
                    Location: {stationAssignments[0]?.location_code || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Assignments Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Session #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Operator
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Product
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Target (kg)
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actual (kg)
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Progress
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Started At
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {stationAssignments.map((assignment: ProductionAssignment, index: number) => {
                      const progress = getProgressPercentage(
                        Number(assignment.actual_output_weight || 0),
                        Number(assignment.target_output_weight || 0)
                      );
                      return (
                        <tr key={`${assignment.session_number}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {assignment.session_number}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                            <div>
                              <div className="font-medium">{assignment.assigned_operator || 'Unassigned'}</div>
                              {assignment.assigned_operator && (
                                <div className="text-xs text-gray-500 dark:text-gray-400">Operator</div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                            <div>
                              <div className="font-medium">{assignment.product_name}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{assignment.product_sku}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                            {Number(assignment.target_output_weight || 0).toFixed(2)} kg
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-right">
                            {Number(assignment.actual_output_weight || 0).toFixed(2)} kg
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2 max-w-[100px]">
                                <div
                                  className="bg-indigo-600 dark:bg-indigo-400 h-2 rounded-full transition-all"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium">{progress}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {formatDateTime(assignment.started_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(assignment.status)}`}>
                              {t(`status.${assignment.status}`) || assignment.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductionAssignments;
